import { supabase } from './supabase';
import { ESPERAS_PDF_UNIFICADO_MS, TENTATIVAS_PDF_UNIFICADO, falhaPassageira } from './pdfUnificadoGran';

/**
 * Resolve o gateway de pagamento (banco_conta) de uma empresa parceira. Faturas
 * sem empresa_parceira_id (empresa_parceira_id null = fatura da própria Vigor
 * Energy) sempre resolvem como não-Asaas — seguem o fluxo Sicoob/n8n de sempre.
 *
 * Vai por RPC, não por `select` direto em `empresas_parceiras`: o RLS da tabela
 * (`ep_adm_all` + `ep_self_select`) só deixa **adm** e usuários da própria
 * parceira lerem. Para um analista de Faturamento a consulta voltava VAZIA — sem
 * erro —, o código lia isso como "não é de parceira" e a emissão caía no caminho
 * do Sicoob, cobrando na conta da Vigor uma fatura que é da Gran (foi o que
 * aconteceu em 10/08/2026 nos boletos 4601 e 4603).
 *
 * A função devolve só o nome do banco; percentual_vigor/percentual_parceira
 * continuam protegidos pelo RLS.
 */
export async function resolveBancoGateway(empresaParceiraId: string | null | undefined): Promise<string | null> {
    if (!empresaParceiraId) return null;
    const { data, error } = await supabase
        .rpc('gateway_da_parceira', { p_empresa_id: empresaParceiraId });
    if (error) {
        // Não silenciar: cair no fluxo errado significa cobrar na conta errada.
        console.error('Falha ao resolver o gateway da empresa parceira:', error.message);
        throw new Error('Não foi possível identificar o banco da empresa parceira desta fatura. Recarregue a página e tente de novo.');
    }
    return (data as string | null) ?? null;
}

async function invokeAsaasCobranca(action: string, payload: Record<string, unknown>) {
    const { data, error } = await supabase.functions.invoke('asaas-cobranca', { body: { action, ...payload } });
    if (error) {
        // Em resposta não-2xx o invoke devolve só "Edge Function returned a non-2xx
        // status code" e joga o corpo em error.context — que é justamente onde está o
        // motivo real ("cobrança já recebida", "credenciais não configuradas"...).
        // Sem isto o usuário vê um erro genérico e ninguém sabe o que aconteceu.
        const detalhe = await (error as any)?.context?.json?.().catch(() => null);
        if (detalhe?.error) throw new Error(detalhe.error);
        throw error;
    }
    if (data?.error) throw new Error(data.error);
    return data;
}

/**
 * `ambiente: 'sandbox'` emite na conta de testes do Asaas: nenhum dinheiro se move e a
 * fatura nasce marcada como sandbox (invisível fora do card de admin em Faturas). A
 * function rejeita sandbox para quem não é adm — o parâmetro aqui é só a intenção.
 */
export async function criarCobrancaAsaas(
    faturaId: string | number,
    billingType: 'PIX' | 'BOLETO',
    ambiente: 'producao' | 'sandbox' = 'producao',
) {
    return invokeAsaasCobranca('criar', { fatura_id: faturaId, billing_type: billingType, ambiente });
}

/**
 * Emissão de teste para faturas do caminho Sicoob (as da própria Vigor). Não chama
 * banco nenhum: o sandbox do Sicoob é só um catálogo de mocks e não registra boleto
 * (POST devolve 400 até com corpo vazio). A function preenche a fatura com dados
 * fictícios não pagáveis e a marca como sandbox — ver emitir-boleto-teste.
 */
export async function emitirBoletoTeste(faturaId: string | number) {
    const { data, error } = await supabase.functions.invoke('emitir-boleto-teste', {
        body: { fatura_id: faturaId },
    });
    if (error) {
        const detalhe = await (error as any)?.context?.json?.().catch(() => null);
        if (detalhe?.error) throw new Error(detalhe.error);
        throw error;
    }
    if (data?.error) throw new Error(data.error);
    return data;
}

export async function cancelarCobrancaAsaas(faturaId: string | number, motivo: string) {
    return invokeAsaasCobranca('cancelar', { fatura_id: faturaId, motivo });
}

export async function prorrogarCobrancaAsaas(faturaId: string | number, novaDataLimite: string) {
    return invokeAsaasCobranca('prorrogar', { fatura_id: faturaId, nova_data_limite: novaDataLimite });
}

/**
 * Gera o PDF combinado do boleto único da Gran (layout da Gran + fatura da Equatorial
 * com marca d'água), a partir dos dados já salvos pela cobrança Asaas. Chamar só depois
 * de criarCobrancaAsaas ter concluído com sucesso.
 *
 * Repete sozinha quando a falha é passageira (banco ou login lentos). Em 20/10/2026 a
 * fatura 14510 ficou sem o PDF porque o login demorou 30 s numa única tentativa — ver
 * pdfUnificadoGran.ts. Repetir é seguro: a function sobrescreve o mesmo arquivo no
 * Storage e não mexe na cobrança.
 */
export async function gerarBoletoGran(faturaId: string | number) {
    let ultimoErro: Error = new Error('Erro ao gerar o PDF unificado.');
    for (let tentativa = 1; tentativa <= TENTATIVAS_PDF_UNIFICADO; tentativa++) {
        if (tentativa > 1) {
            await new Promise((r) => setTimeout(r, ESPERAS_PDF_UNIFICADO_MS[tentativa - 2] ?? 8000));
        }
        const { data, error } = await supabase.functions.invoke('gerar-boleto-gran', { body: { fatura_id: faturaId } });
        if (!error) {
            if (data?.error) throw new Error(data.error);
            return data;
        }
        // Mesmo motivo de invokeAsaasCobranca: a causa real vem em error.context.
        const status: number | undefined = (error as any)?.context?.status;
        const detalhe = await (error as any)?.context?.json?.().catch(() => null);
        ultimoErro = status === 401
            // A function diz só "Unauthorized", que não ajuda quem está na tela.
            ? new Error('O servidor não confirmou seu login a tempo (pode estar lento ou a sessão expirou). Tente de novo em instantes; se continuar, saia e entre de novo.')
            : new Error(detalhe?.error || error.message);
        if (!falhaPassageira(status)) break;
    }
    throw ultimoErro;
}

/**
 * `'v6'` é o layout novo do boleto unificado — desenhado do zero, sem o template
 * antigo. Continua sendo só desenho: nada muda na cobrança nem no que o cliente
 * recebe hoje (o envio segue usando o layout 'legado').
 */
export type LayoutBoletoVigor = 'legado' | 'v6';

/**
 * Monta o PDF do boleto da própria Vigor e devolve os bytes. Diferente do da Gran,
 * este PDF não fica salvo em lugar nenhum — a function o desenha a cada chamada.
 *
 * Vai por fetch, e não por supabase.functions.invoke, de propósito: o invoke escolhe
 * como ler a resposta pelo Content-Type e só trata 'application/octet-stream' como
 * binário. Como gerar-pdf-boleto responde 'application/pdf', o invoke cairia em
 * response.text() e corromperia o arquivo.
 */
export async function gerarPdfBoletoVigor(
    faturaId: string | number,
    layout: LayoutBoletoVigor = 'legado',
    /** Só no v6: força o selo "Bom Pagador". Omitido = a function decide pelo histórico. */
    selo?: boolean,
): Promise<Blob> {
    const { data: { session } } = await supabase.auth.getSession();
    if (!session?.access_token) throw new Error('Sessão expirada. Entre novamente.');

    const res = await fetch(`${import.meta.env.VITE_SUPABASE_URL}/functions/v1/gerar-pdf-boleto`, {
        method: 'POST',
        headers: {
            'Content-Type': 'application/json',
            apikey: import.meta.env.VITE_SUPABASE_ANON_KEY,
            Authorization: `Bearer ${session.access_token}`,
        },
        body: JSON.stringify({ record_id: faturaId, layout, ...(selo === undefined ? {} : { selo }) }),
    });

    if (!res.ok) {
        // A function devolve JSON no erro e PDF no sucesso.
        const msg = await res.json().catch(() => null);
        throw new Error(msg?.error || `Falha ao gerar o PDF (status ${res.status}).`);
    }
    return res.blob();
}

/**
 * Desenha o PDF do boleto da Vigor, publica no site institucional e grava
 * `faturas.url_boleto`. Devolve a URL pública.
 *
 * É o conserto do boleto que existe no Sicoob mas ficou sem PDF. O fluxo n8n de
 * emissão registra a cobrança primeiro e só depois desenha o PDF; quando esse passo
 * cai (um 502 do Supabase basta, e o nó não tinha retry), o resto do fluxo nunca
 * roda e `url_boleto` fica nula com a cobrança viva. Sem isto a única saída seria
 * cancelar e reemitir -- criando uma segunda cobrança para o mesmo cliente.
 *
 * Não toca em valor, nosso número, linha digitável nem Pix: só redesenha com os
 * dados que já estão na fatura e põe o arquivo no ar.
 */
export async function publicarPdfBoletoVigor(faturaId: string | number): Promise<string> {
    const { data: { session } } = await supabase.auth.getSession();
    if (!session?.access_token) throw new Error('Sessão expirada. Entre novamente.');

    const res = await fetch(`${import.meta.env.VITE_SUPABASE_URL}/functions/v1/gerar-pdf-boleto`, {
        method: 'POST',
        headers: {
            'Content-Type': 'application/json',
            apikey: import.meta.env.VITE_SUPABASE_ANON_KEY,
            Authorization: `Bearer ${session.access_token}`,
        },
        // `publicar` troca a resposta de PDF para JSON -- por isso este caminho
        // não reaproveita gerarPdfBoletoVigor.
        body: JSON.stringify({ record_id: faturaId, layout: 'legado', publicar: true }),
    });

    const corpo = await res.json().catch(() => null);
    if (!res.ok || !corpo?.url) {
        throw new Error(corpo?.error || `Falha ao publicar o PDF do boleto (status ${res.status}).`);
    }
    return String(corpo.url);
}
